import { Link } from "react-router-dom";
import { ArrowUpRight, MessageCircle, Download } from "lucide-react";
import PageHeader from "../components/PageHeader";
import CTABanner from "../components/CTABanner";
import { Reveal } from "../components/motion";
import useMeta from "../lib/useMeta";
import { breadcrumb } from "../lib/seo";
import { SERVICES, SERVICE_PAGES, CATEGORY_IMAGES, CATALOGUE_URL, waLink } from "../lib/site";

export default function Products() {
  useMeta({
    title: "Procurement & Workplace Supplies | Saarthva Ventures",
    description: "Source housekeeping, pantry, stationery, MRO, IT, furniture, PPE and specialised business requirements through Saarthva.",
    path: "/products",
    schemas: [breadcrumb("Procurement & Supplies", "/products")],
  });
  return (
    <div data-testid="products-page">
      <PageHeader
        eyebrow="PROCUREMENT & SUPPLIES"
        title="Everyday essentials to"
        accent="specialised sourcing."
        description="OPEX, CAPEX, MRO and one-time setups — one partner, one quotation, one point of accountability. Browse the categories, then send us your requirement."
      />

      <section className="bg-white" data-testid="products-grid-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-16">
            <div>
              <p className="font-mono text-[11px] tracking-[0.3em] text-steel mb-4">PROCUREMENT CATEGORIES</p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-navy leading-tight">
                Everyday essentials to<br />specialised sourcing.
              </h2>
            </div>
            <a
              href={CATALOGUE_URL}
              data-testid="products-catalogue-download"
              className="inline-flex items-center gap-2 rounded-full border border-navy/20 hover:border-saffron text-navy text-sm font-semibold px-5 py-2.5 transition-colors"
            >
              <Download className="w-4 h-4 text-saffron" /> Download Line Card (PDF)
            </a>
          </Reveal>

          <div className="space-y-16 lg:space-y-24">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              const flip = i % 2 === 1;
              return (
                <Reveal key={s.id} delay={0.05}>
                  <div id={s.id} data-testid={`product-card-${s.id}`} className="group grid lg:grid-cols-2 gap-8 lg:gap-14 items-center scroll-mt-28">
                    <div className={`relative ${flip ? "lg:order-2" : ""}`}>
                      <div className="absolute -inset-2 rounded-3xl border border-saffron/25 translate-x-3 translate-y-3" />
                      <div className="relative overflow-hidden rounded-3xl">
                        <img
                          src={CATEGORY_IMAGES[s.id]}
                          alt={`${s.title} — ${s.tags.join(", ")}`}
                          data-testid={`product-image-${s.id}`}
                          loading="lazy"
                          className="w-full h-64 sm:h-80 object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute top-4 left-4 flex items-center gap-2">
                          <span className="rounded-full bg-navy/85 backdrop-blur text-white font-mono text-[10px] tracking-widest px-3 py-1.5">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <span className="rounded-full bg-saffron text-white font-mono text-[10px] tracking-widest px-3 py-1.5" data-testid={`product-class-${s.id}`}>
                            {s.cls}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className={flip ? "lg:order-1" : ""}>
                      <span className="w-12 h-12 rounded-xl bg-cream grid place-items-center mb-6 transition-colors group-hover:bg-saffron">
                        <Icon className="w-5 h-5 text-saffron transition-colors group-hover:text-white" strokeWidth={1.8} />
                      </span>
                      <h3 className="font-display text-2xl sm:text-3xl font-bold text-navy">{s.title}</h3>
                      <p className="mt-3 text-slate-600 leading-relaxed max-w-lg">{s.desc}</p>
                      <div className="mt-5 flex flex-wrap gap-2">
                        {s.tags.map((t) => (
                          <span key={t} className="text-xs font-medium text-slate-600 bg-mist border border-line rounded-full px-3 py-1.5">
                            {t}
                          </span>
                        ))}
                      </div>
                      <Link
                        to={`/request-quote?category=${s.id}`}
                        data-testid={`product-enquire-${s.id}`}
                        className="mt-7 inline-flex items-center gap-1.5 rounded-full bg-navy hover:bg-saffron text-white text-sm font-semibold px-5 py-2.5 transition-colors"
                      >
                        Enquire for this category <ArrowUpRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <div className="grid md:grid-cols-2 gap-5 mt-20">
            {SERVICE_PAGES.map((s) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.path}>
                  <Link
                    to={s.path}
                    data-testid={`service-link-${s.path.slice(1)}`}
                    className="group flex flex-col h-full rounded-2xl bg-navy text-white p-8 sm:p-10 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
                  >
                    <span className="w-12 h-12 rounded-xl bg-white/10 grid place-items-center mb-6 group-hover:bg-saffron transition-colors">
                      <Icon className="w-5 h-5 text-saffron group-hover:text-white transition-colors" strokeWidth={1.8} />
                    </span>
                    <p className="font-mono text-[10px] tracking-[0.3em] text-saffron mb-2">SERVICE</p>
                    <h3 className="font-display text-2xl font-bold">{s.title}</h3>
                    <p className="mt-2 text-sm text-slate-300 leading-relaxed flex-1">{s.desc}</p>
                    <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-saffron">
                      Explore the service <ArrowUpRight className="w-4 h-4" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-12 rounded-2xl bg-cream border border-saffron/30 p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6" data-testid="custom-sourcing-note">
              <div>
                <h3 className="font-display text-xl sm:text-2xl font-bold text-navy">Cannot find it on this list?</h3>
                <p className="mt-2 text-slate-600 max-w-xl">
                  If your requirement does not fit a category, send it to us anyway. Our sourcing model is built
                  for the unusual, urgent and hard-to-find — or route it through <Link to="/onedesk" className="font-semibold text-saffron hover:text-navy transition-colors">Saarthva OneDesk</Link> and a Saarthi owns it till closure.
                </p>
              </div>
              <a
                href={waLink("Hi Saarthva, I'm looking for something specific:")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="custom-sourcing-whatsapp-button"
                className="inline-flex items-center gap-2 rounded-full bg-saffron hover:bg-saffrondark text-white font-semibold px-6 py-3 transition-colors shrink-0"
              >
                <MessageCircle className="w-4 h-4" /> Ask Saarthva
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
